import React, { useEffect, useState } from 'react';
import './devis.css';
import axios from 'axios';
import { NewSideBarAdmin } from '../../NewSideBarAdmin';
import { formatCodeDevis } from '../../../Services/CodeTrackingHelper';

interface Devis {
  id: number;
  DevisId: number | string;
  RefDevis?: string;
  client: string;
  MagasinName: string;
  RefArticle: string;
  designation: string;
  qte: number;
  prix: number;
  total: number;
  status: 'confirmé' | 'en cours' | 'refusé' | string;
  date: string;
  status_payment?: string;
}

const STATUS_META: Record<string, { label: string; stamp: string; tone: string }> = {
  'confirmé':  { label: 'Confirmé', stamp: 'APPROUVÉ',  tone: 'accepted' },
  'en cours':  { label: 'En cours', stamp: 'EN ATTENTE', tone: 'pending'  },
  'refusé':    { label: 'Refusé',   stamp: 'REFUSÉ',     tone: 'refused'  },
};

const getStatusMeta = (status: string) =>
  STATUS_META[status] ?? { label: status || 'N/A', stamp: (status || 'N/A').toUpperCase(), tone: 'neutral' };

const DevisHome: React.FC = () => {
  const [devisList, setDevisList] = useState<Devis[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedDevis, setSelectedDevis] = useState<Devis | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchDevis = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_PHP_APP_URL}/admin/devis`);
        setDevisList(response.data);
      } catch (error) {
        console.error('Erreur lors du chargement des devis :', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDevis();
  }, []);

  const totalDevis = devisList.length;
  const accepted = devisList.filter((d) => d.status === 'confirmé').length;
  const pending = devisList.filter((d) => d.status === 'en cours').length;
  const refused = devisList.filter((d) => d.status === 'refusé').length;

  const filteredDevis = devisList.filter((devis) => {
    const matchSearch =
      devis.client?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      devis.id?.toString().includes(searchTerm) ||
      devis.DevisId?.toString().includes(searchTerm) ||
      (devis.RefDevis ?? '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter ? devis.status === statusFilter : true;
    return matchSearch && matchStatus;
  });

  const handleShowDrawer = (id: number) => {
    const devis = devisList.find((d) => d.id === id) || null;
    setSelectedDevis(devis);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setSelectedDevis(null), 280);
  };

  const handleDeleteClick = (id: number) => {
    setDeleteConfirm(id);
  };

  const confirmDelete = async (id: number) => {
    try {
      await axios.delete(`${process.env.REACT_APP_PHP_APP_URL}/admin/devis/${id}`);
      setDevisList(devisList.filter((d) => d.id !== id));
      if (selectedDevis?.id === id) {
        handleCloseDrawer();
      }
      setDeleteConfirm(null);
      setSuccessMessage("Devis supprimé avec succès !");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Erreur lors de la suppression du devis :", error);
      alert("Une erreur est survenue lors de la suppression.");
      setDeleteConfirm(null);
    }
  };

  const formatDate = (date: string, long = false) =>
    date
      ? new Date(date).toLocaleDateString('fr-FR', long ? { day: '2-digit', month: 'long', year: 'numeric' } : undefined)
      : '—';

  const stats = [
    { key: 'total', label: 'Total devis', value: totalDevis, tone: 'ink' },
    { key: 'accepted', label: 'Approuvés', value: accepted, tone: 'accepted' },
    { key: 'pending', label: 'En attente', value: pending, tone: 'pending' },
    { key: 'refused', label: 'Refusés', value: refused, tone: 'refused' },
  ];

  return (
    <>
      <NewSideBarAdmin />

      <div className="ldg-shell">
        {drawerOpen && <div className="ldg-scrim" onClick={handleCloseDrawer} />}

        {/* ---- Detail drawer ---- */}
        <aside className={`ldg-drawer ${drawerOpen ? 'ldg-drawer--open' : ''}`} aria-hidden={!drawerOpen}>
          <div className="ldg-drawer-head">
            <span className="ldg-drawer-eyebrow">Fiche du devis</span>
            <button className="ldg-icon-btn" onClick={handleCloseDrawer} aria-label="Fermer">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {selectedDevis ? (
            <div className="ldg-drawer-body">
              <div className="ldg-drawer-top">
                <span className="ldg-mono ldg-drawer-id">{formatCodeDevis(selectedDevis.RefDevis, selectedDevis.id, selectedDevis.date)}</span>
                <span className={`ldg-stamp ldg-stamp--${getStatusMeta(selectedDevis.status).tone} ldg-stamp--lg`}>
                  {getStatusMeta(selectedDevis.status).stamp}
                </span>
              </div>

              <section className="ldg-section">
                <h3 className="ldg-section-title">Article</h3>
                <dl className="ldg-deflist">
                  <div className="ldg-defrow">
                    <dt>Référence</dt><dd className="ldg-mono">{selectedDevis.RefArticle}</dd>
                  </div>
                  <div className="ldg-defrow">
                    <dt>Désignation</dt><dd>{selectedDevis.designation}</dd>
                  </div>
                  <div className="ldg-defrow">
                    <dt>Quantité</dt><dd className="ldg-mono">{selectedDevis.qte}</dd>
                  </div>
                  <div className="ldg-defrow">
                    <dt>Prix unitaire</dt><dd className="ldg-mono">{selectedDevis.prix} MAD</dd>
                  </div>
                </dl>
              </section>

              <section className="ldg-section">
                <h3 className="ldg-section-title">Commande</h3>
                <dl className="ldg-deflist">
                  <div className="ldg-defrow">
                    <dt>Client</dt><dd>{selectedDevis.client}</dd>
                  </div>
                  <div className="ldg-defrow">
                    <dt>Magasin</dt><dd>{selectedDevis.MagasinName}</dd>
                  </div>
                  <div className="ldg-defrow">
                    <dt>Date</dt><dd className="ldg-mono">{formatDate(selectedDevis.date, true)}</dd>
                  </div>
                  <div className="ldg-defrow">
                    <dt>Paiement</dt>
                    <dd>
                      <span className={`ldg-stamp ldg-stamp--${selectedDevis.status_payment === 'payé' ? 'accepted' : 'refused'}`}>
                        {selectedDevis.status_payment === 'payé' ? 'PAYÉ' : 'NON PAYÉ'}
                      </span>
                    </dd>
                  </div>
                </dl>
              </section>

              <div className="ldg-drawer-total">
                <span>Total du devis</span>
                <strong className="ldg-mono">{selectedDevis.total} MAD</strong>
              </div>
            </div>
          ) : (
            <div className="ldg-drawer-loading"><span className="ldg-spinner" /></div>
          )}
        </aside>

        {/* ---- Main ---- */}
        <main className="ldg-main">
          <header className="ldg-header">
            <div>
              <p className="ldg-eyebrow">Administration</p>
              <h1 className="ldg-title">Registre des devis</h1>
            </div>
            <span className="ldg-mono ldg-header-count">{totalDevis} entrées</span>
          </header>

          {/* Stat tabs */}
          <div className="ldg-stats">
            {stats.map((s) => (
              <div className={`ldg-stat ldg-stat--${s.tone}`} key={s.key}>
                <span className="ldg-stat-value ldg-mono">{loading ? '—' : s.value}</span>
                <span className="ldg-stat-label">{s.label}</span>
              </div>
            ))}
          </div>

          {/* Toolbar */}
          <div className="ldg-toolbar">
            <div className="ldg-search">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Rechercher par client ou numéro…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="ldg-search-clear" onClick={() => setSearchTerm('')} aria-label="Effacer la recherche">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>

            <div className="ldg-tabs" role="tablist">
              {[
                { value: '', label: 'Tous' },
                { value: 'confirmé', label: 'Approuvés' },
                { value: 'en cours', label: 'En attente' },
                { value: 'refusé', label: 'Refusés' },
              ].map((tab) => (
                <button
                  key={tab.value}
                  role="tab"
                  aria-selected={statusFilter === tab.value}
                  className={`ldg-tab ${statusFilter === tab.value ? 'ldg-tab--active' : ''}`}
                  onClick={() => setStatusFilter(tab.value)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {(searchTerm || statusFilter) && !loading && (
            <p className="ldg-results-count">
              {filteredDevis.length} résultat{filteredDevis.length !== 1 ? 's' : ''}
            </p>
          )}

          {/* Content */}
          {loading ? (
            <div className="ldg-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div className="ldg-slip ldg-slip--skeleton" key={i}>
                  <div className="ldg-skel-line ldg-skel-line--sm" />
                  <div className="ldg-skel-line" />
                  <div className="ldg-skel-line" />
                  <div className="ldg-skel-line ldg-skel-line--sm" />
                </div>
              ))}
            </div>
          ) : filteredDevis.length > 0 ? (
            <div className="ldg-grid">
              {filteredDevis.map((devis, index) => {
                const meta = getStatusMeta(devis.status);
                return (
                  <article className="ldg-slip" key={devis.id ?? index} style={{ animationDelay: `${index * 35}ms` }}>
                    <div className="ldg-slip-perf" aria-hidden="true" />

                    <div className="ldg-slip-top">
                      <span className="ldg-mono ldg-slip-id">{formatCodeDevis(devis.RefDevis, devis.id, devis.date)}</span>
                      <span className={`ldg-stamp ldg-stamp--${meta.tone}`}>{meta.stamp}</span>
                    </div>

                    <div className="ldg-slip-rows">
                      <div className="ldg-slip-row">
                        <span className="ldg-slip-row-label">Client</span>
                        <span className="ldg-slip-row-value">{devis.client}</span>
                      </div>
                      <div className="ldg-slip-row">
                        <span className="ldg-slip-row-label">Magasin</span>
                        <span className="ldg-slip-row-value">{devis.MagasinName}</span>
                      </div>
                      <div className="ldg-slip-row">
                        <span className="ldg-slip-row-label">Date</span>
                        <span className="ldg-slip-row-value ldg-mono">{formatDate(devis.date)}</span>
                      </div>
                      <div className="ldg-slip-row">
                        <span className="ldg-slip-row-label">Paiement</span>
                        <span className={`ldg-stamp ldg-stamp--${devis.status_payment === 'payé' ? 'accepted' : 'refused'}`} style={{ fontSize: '0.65rem', padding: '1px 6px', lineHeight: 1 }}>
                          {devis.status_payment === 'payé' ? 'Payé' : 'Non payé'}
                        </span>
                      </div>
                    </div>

                    <div className="ldg-slip-foot">
                      <div className="ldg-slip-total">
                        <span>Total</span>
                        <strong className="ldg-mono">{devis.total} MAD</strong>
                      </div>
                      <div className="ldg-slip-actions">
                        <button className="ldg-btn ldg-btn--view" onClick={() => handleShowDrawer(devis.id)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                          </svg>
                          Détails
                        </button>
                        <button className="ldg-btn ldg-btn--delete" title="Supprimer" aria-label="Supprimer" onClick={() => handleDeleteClick(devis.id)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="ldg-empty">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              <h3>Aucun devis trouvé</h3>
              <p>Modifiez vos critères de recherche ou réinitialisez les filtres.</p>
              <button className="ldg-btn ldg-btn--ghost" onClick={() => { setSearchTerm(''); setStatusFilter(''); }}>
                Réinitialiser les filtres
              </button>
            </div>
          )}
        </main>
      </div>

      {/* --- Delete Confirmation Modal --- */}
      {deleteConfirm !== null && (
        <div className="devis-modal-overlay">
          <div className="devis-modal">
            <h3>Confirmer la suppression</h3>
            <p>Êtes-vous sûr de vouloir supprimer ce devis de manière définitive ?</p>
            <div className="devis-modal-actions">
              <button className="devis-btn-cancel" onClick={() => setDeleteConfirm(null)}>Annuler</button>
              <button className="devis-btn-confirm" onClick={() => confirmDelete(deleteConfirm)}>Oui, supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* --- Success Toast --- */}
      {successMessage && (
        <div className="devis-toast">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          {successMessage}
        </div>
      )}
    </>
  );
};

export default DevisHome;